// Use if we want to enable chunks in the future
// eslint-disable-next-line no-undef
// __webpack_public_path__ = window.assetsPath;

import deepmerge from "deepmerge";
import { h, Fragment, render } from 'preact';

import { Snap } from "@searchspring/snap-preact";
import { getContext, DomTargeter } from "@searchspring/snap-toolbox";
import { tabManager } from "./middleware/plugins/tabManager";

import { afterStore } from "./middleware/plugins/afterStore";
import { configurable } from "./middleware/plugins/configurable";
import { combineMerge } from "./middleware/functions";

// context from script tag
const context = getContext(['collection', 'tags', 'template', 'shopper', 'format', 'blog', 'region', 'language', 'myShopifyDomain']);
const locale = window.Shopify.locale;
const myShopifyDomain = context.myShopifyDomain?.url;

const productFeedIdPath = window.Resources.searchspring.siteIds.productFeeds;
const contentFeedIdPath = window.Resources.searchspring.siteIds.contentFeeds;

//Defaults - US English
let siteIdProducts = "lz6dnx";
let siteIdContent = "kfqm2s";

//Change siteId if we are on another instance
if(context.region == "ca") {
	if(locale == "fr") {
		siteIdProducts = "77w18f";
		siteIdContent = "5cfswg";
	}
	else {
		siteIdProducts = "nzj0kg";
		siteIdContent = "zsq3zk";
	}
}
else if(context.region == "uk") {
	siteIdProducts = "s8k4tm";
	siteIdContent = "wkpl02";
}

//Update if we are on the Staging enviornment
if(myShopifyDomain.includes("staging")) {
	siteIdProducts = productFeedIdPath.staging;
	siteIdContent = contentFeedIdPath.staging;
}

/* create settings config for re-usable variables */
const settings = {
	products: {
		id: 'products',
		label: window.Resources.searchspring.typeahead.productTab,
		labelPage: window.Resources.searchspring.results.resultsProductsCountPlural,
		siteId: siteIdProducts,
		icon: '',
	},
	articles: {
		id: 'articles',
		label: window.Resources.searchspring.results.resultsArticlesCountPlural,
		labelPage: window.Resources.searchspring.results.resultsArticlesCountPlural,
		siteId: siteIdContent,
		icon: '',
	},
	search: {
		page: window.location.href.includes('/search') ? true : false,
	},
	ac: {
		input: '.header__search-desktop .search__input',
		target: '.ss__autocomplete--target',
		after: '.header__search-desktop .search-modal__form',
    input_mobile:'.header__search-mobile .search__input',
    after_mobile: '.header__search-mobile .search-modal__form'
	},
};

/* set up site details config */
let site = {
	id: context?.siteId ? context.siteId : settings.products.siteId,
	currency: 'usd',
	lang: 'en',
	parameters: {
		query: 'q',
		page: 'page',
	},
	features: {
		integratedSpellCorrection: {
			enabled: true,
		},
	},
};

/* set up page details config */
let page = {
	id: settings.search.page ? 'search' : 'other',
	title: settings.search.page ? 'Search Results' : 'Other Page',
	type: settings.search.page ? 'search' : 'other',
};

/* background filters */
let backgroundFilters = [];
let backgroundFiltersArticles = [];


if (context.collection?.handle) {

  // update page details when on collection
  page = {
    id: context.collection.handle,
    title: context.collection.name,
    type: 'collection',
  };

  // set background filterss
  if (context.collection.handle != 'all') {
    backgroundFilters.push({
      field: 'collection_handle',
      value: context.collection.handle,
      type: 'value',
      background: true,
    });
  }

  // handle collection tags (filters)
  if (context.tags) {
    const collectionTags = context.tags
      .toLowerCase()
			.replace(/\&quot\;/g, '"')
      .replace(/-/g, '')
      .replace(/ +/g, '')
      .split('|');
    collectionTags.forEach((tag) => {
      backgroundFilters.push({
        field: 'ss_tags',
        value: tag,
        type: 'value',
        background: true,
      });
    });
  }
}

if (context.blog) {
  backgroundFiltersArticles.push({
		field: 'tags',
		value: 'blog-tag',
		type: 'value',
		background: true,
	});
}

//Determine Market
const currentUrl = window.location.href;

//Apply BG filter(s).
//Searchspring did some behind-the-scenes stuff to make the array appear as such when returned.
//Normally, it returns kind of goofy because you have to separate by comma or something.

//If market is UK
if(currentUrl.includes(".uk")) {

	//Show UK Products only
	backgroundFilters.push({
		field: "mfield_cql_market",
		value: "UK",
		type: "value",
		background: true,
	});
}
//If market is EU
else if(currentUrl.includes(".eu")) {

	//Show EU Products only
	backgroundFilters.push({
		field: "mfield_cql_market",
		value: "EU",
		type: "value",
		background: true,
	});
}
//If market is AU
else if(currentUrl.includes(".au")) {

	//Show AU Products only
	backgroundFilters.push({
		field: "mfield_cql_market",
		value: "AU",
		type: "value",
		background: true,
	});
}

/*
	configuration and instantiation
  Propel (test data) siteID - zw7x36
 */

let config = {
	context,
  url: {
    settings: {
      coreType: "query",
      customType: "query",
    },
    parameters: {
      core: {
        query: { name: "q" },
        page: { name: "page" },
      },
    },
  },
  client: {
    globals: {
      siteId: siteIdProducts,
    },
  },
	features: site.features,
  instantiators: {
    recommendation: {
      components: {
        Default: async () => {
          return (await import("./components/Recommendations/Recs/Recs")).Recs;
        },
      },
      config: {
        branch: 'develop',
        plugins: [site, page, [afterStore], [configurable]],
      },
    },
  },
  controllers: {
    search: [
      {
        url: {
					parameters: {
						core: {
							sort: { name: 'psort' },
							filter: { name: 'pfilter' },
							page: { name: 'ppage' },
						},
					},
				},
        config: {
          id: 'products',
          plugins: [site, page, [afterStore], [configurable]],
          isSearch: settings.search.page,
          globals: {
            filters: backgroundFilters,
            pagination: {
              pageSize: window?.ssConfig?.pageSize ?? 24,
            },
            ssConfig: window?.ssConfig,
          },
          settings: {
						redirects: {
							singleResult: false,
						},
						infinite: { // Add this object to enable appending results to existing results rendered
							backfill: 5, // This is default value, see documentation surrounding backfill
						}
					},
        },
      },
      {
				url: {
					parameters: {
						core: {
							sort: { name: 'asort' },
							filter: { name: 'afilter' },
							page: { name: 'apage' },
						},
					},
				},
				config: {
					id: settings.articles.id,
					plugins: [site, page, [afterStore], [configurable]],
					isSearch: settings.search.page,
					globals: {
						filters: backgroundFiltersArticles,
						siteId: settings.articles.siteId,
					},
					settings: {
						redirects: {
							singleResult: false,
						},
						infinite: {
							backfill: 5,
						}
					},
				},
			},
    ],
    autocomplete: [
      {
        config: {
          id: 'ac-products',
          selector: settings.ac.input,
					plugins: [site, page, [afterStore], [configurable]],
          settings: {
						history: {
							limit: 6,
							showResults: true,
						},
						trending: {
							limit: 6,
							showResults: true,
						},
					},
					globals: {
						filters: backgroundFilters,
						facets: {
							limit: 5,
							valueLimit: 10,
						},
						pagination: {
							pageSize: 6,
						},
						merchandising: {
							disableInlineBanners: true,
						},
					},
				},
			},
      {
				config: {
					id: 'ac-articles',
					selector: settings.ac.input,
					plugins: [site, page, [afterStore], [configurable]],
					settings: {
						history: {
							limit: 6,
							showResults: true,
						},
						trending: {
							limit: 6,
							showResults: true,
						},
					},
					globals: {
						siteId: settings.articles.siteId,
						facets: {
							limit: 5,
							valueLimit: 10,
						},
						pagination: {
							pageSize: 6,
						},
					},
				},
			},
      {
				config: {
					id: 'ac-products-mobile',
					selector: settings.ac.input_mobile,
					plugins: [site, page, [afterStore], [configurable]],
					settings: {
						history: {
							limit: 6,
							showResults: true,
						},
						trending: {
							limit: 6,
							showResults: true,
						},
					},
					globals: {
						filters: backgroundFilters,
						facets: {
							limit: 5,
							valueLimit: 10,
						},
						pagination: {
							pageSize: 6,
						},
						merchandising: {
							disableInlineBanners: true,
						},
					},
				},
			},
			{
				config: {
					id: 'ac-articles-mobile',
					selector: settings.ac.input_mobile,
					plugins: [site, page, [afterStore], [configurable]],
					settings: {
						history: {
							limit: 6,
							showResults: true,
						},
						trending: {
							limit: 6,
							showResults: true,
						},
					},
					globals: {
						siteId: settings.articles.siteId,
						facets: {
							limit: 5,
							valueLimit: 10,
						},
						pagination: {
							pageSize: 6,
						},
					},
				},
			},
    ],
    recommendation: [
			{
				config: {
					id: 'no-results',
					tag: '404',
					branch: 'develop',
					plugins: [site, page, [afterStore], [configurable]],
				},
			},
		],
    finder: [
      {
        config: {
          id: "finder",
          url: "/",
          fields: [
            {
              field: "tags_atrb_colorfilter",
              label: "Color",
            },
          ],
        },
        targeters: [
          {
            name: "finder",
            selector: "#searchspring-finder",
            component: async () => {
              return (await import("./components/Finder/Finder")).Finder;
            },
          },
        ],
      },
      {
        config: {
          id: "finder_hierarchy",
          url: "/",
          fields: [
            {
              field: "ss_category_hierarchy",
            },
          ],
        },
        targeters: [
          {
            name: "finder_hierarchy",
            selector: "#searchspring-finder-hierarchy",
            component: async () => {
              return (await import("./components/Finder/Finder")).Finder;
            },
          },
        ],
      },
    ],
  },
};

// used to add config settings from cypress e2e tests
if (window?.mergeSnapConfig) {
  config = deepmerge(config, window.mergeSnapConfig, {
    arrayMerge: combineMerge,
  });
}

const snap = new Snap(config);

snap.getControllers('products', 'articles').then(([products, articles, noResults]) => {
	
	/* create search tab manager */
	const tabs = new tabManager({
		getDefault: (tabs) =>  tabs.find(tab => tab.controller.store.pagination.totalResults)?.id,
		param: 'view',
		tabs: [
			{
				id: settings.products.id,
				label: settings.products.label,
				labelPage: settings.products.labelPage,
				controller: products,
				icon: settings.products.icon,
			},
			{
				id: settings.articles.id,
				label: settings.articles.label,
				labelPage: settings.articles.labelPage,
				controller: articles,
				icon: settings.articles.icon,
			},
		],
	});

	new DomTargeter(
		[
			{
				selector: '#searchspring-content',
				component: async () => {
					return (await import('./components/Content/Content')).Content;
				},
				hideTarget: true,
			},
		],
		async (target, element) => {
			const additionalProps = target?.props ? target.props : {};
			const Component = await target.component();
			render(<Component tabs={tabs} {...additionalProps} />, element);
		}
	);

	/* recommendations for no results */
	products.noResultsController = noResults;
	articles.noResultsController = noResults;
});

snap.getControllers('ac-products', 'ac-articles').then(([acProducts, acArticles]) => {
	/* create autocomplete tab manager */
	const acTabs = new tabManager({
		getDefault: (tabs) =>  tabs.find(tab => tab.controller.store.pagination.totalResults)?.id,
		param: 'view',
		tabs: [
			{
				id: settings.products.id,
				label: settings.products.label,
				labelPage: settings.products.labelPage,
				controller: acProducts,
				icon: settings.products.icon,
			},
			{
				id: settings.articles.id,
				label: settings.articles.label,
				labelPage: settings.articles.labelPage,
				controller: acArticles,
				icon: settings.articles.icon,
			},
		],
	});

	new DomTargeter(
		[
			{
				name: 'main',
				selector: settings.ac.after,
				inject: {
					action: 'after', // before, after, append, prepend
					element: () => {
						const acContainer = document.createElement('div');
						acContainer.className = settings.ac.target;
						acContainer.addEventListener('click', (e) => {
							e.stopPropagation();
						});
						return acContainer;
					},
				},
				props: {
					input: settings.ac.input,
				},
			},
		],

		async (target, element, originalElement) => {
			const inputElement = document.querySelector(settings.ac.input);

			const Autocomplete = (await import('./components/Autocomplete/Autocomplete')).Autocomplete;
			render(<Autocomplete input={inputElement} tabs={acTabs} />, element);
			acProducts.unbind();
			acArticles.unbind();
			acTabs.active.controller.bind();
		},
	);
});

snap.getControllers('ac-products-mobile', 'ac-articles-mobile').then(([acProducts, acArticles]) => {
	/* create autocomplete tab manager */
	const acTabs = new tabManager({
		getDefault: (tabs) =>  tabs.find(tab => tab.controller.store.pagination.totalResults)?.id,
		param: 'view',
		tabs: [
			{
				id: settings.products.id,
				label: settings.products.label,
				labelPage: settings.products.labelPage,
				controller: acProducts,
				icon: settings.products.icon,
			},
			{
				id: settings.articles.id,
				label: settings.articles.label,
				labelPage: settings.articles.labelPage,
				controller: acArticles,
				icon: settings.articles.icon,
			},
		],
	});

	new DomTargeter(
		[
      {
				name: 'main-mobile',
				selector: settings.ac.after_mobile,
				inject: {
					action: 'after', // before, after, append, prepend
					element: () => {
						const acContainer = document.createElement('div');
						acContainer.className = settings.ac.target;
						acContainer.addEventListener('click', (e) => {
							e.stopPropagation();
						});
						return acContainer;
					},
				},
				props: {
					input: settings.ac.input_mobile,
				},
			},
		],

		async (target, element, originalElement) => {
			const inputElement = document.querySelector(settings.ac.input_mobile);
			const Autocomplete = (await import('./components/Autocomplete/Autocomplete')).Autocomplete;
			render(<Autocomplete input={inputElement} tabs={acTabs} />, element);
			acProducts.unbind();
			acArticles.unbind();
			acTabs.active.controller.bind();
		},
	);
});
